import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Button, Card, Form, Input, InputNumber, Icon} from 'antd';

import {takeStockTake, getStock, clearStock, getStockTakeCounts} from './actions';
import {formItemLayout} from "../utils/style";
import {bindActionCreators} from "redux";
import {getProduct} from "../Product/actions";
import {decodeParams} from "../utils/params";
import ResponsiveTable from "../common/ResponsiveTable";

const FormItem = Form.Item;
const ButtonGroup = Button.Group;
const TextArea = Input.TextArea;

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some(field => fieldsError[field]);
}

const StockTakeStatuses = {
  Initial: 0,
  Added: 1,
  Error: 2,
};

function stockTakeReducer(stockTakes, upc) {
  let stockTake = stockTakes[upc];
  if (!stockTake) {
    stockTake = {
      status: StockTakeStatuses.Initial,
      upc,
      quantity: 0
    };
  }
  stockTake.quantity += 1;
  stockTakes[upc] = stockTake;
  return stockTakes;
}

// eslint-disable-next-line react/prop-types
const Step1 = ({upcs, onSubmit, onCancel, getFieldDecorator, getFieldsError}) =>
  <Form onSubmit={onSubmit}>
    <FormItem
      {...formItemLayout}
      label="UPCs"
    >
      {getFieldDecorator('upcs', {
        initialValue: upcs,
        rules: [{
          required: true,
          message: 'UPCs are required'
        }]
      })(
        <TextArea rows={33}/>
      )}
    </FormItem>
    <FormItem>
      <ButtonGroup>
        <Button
          type="primary"
          htmlType="submit"
          disabled={hasErrors(getFieldsError())}
        >
          Next
        </Button>
        <Button
          htmlType="button"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </ButtonGroup>
    </FormItem>
  </Form>;

const step2Columns = [
  {title: '', dataIndex: 'status', key: 'status'},
  {title: 'UPC', dataIndex: 'upc', key: 'upc'},
  {title: 'Quantity', dataIndex: 'quantity', key: 'quantity'},
];
// eslint-disable-next-line react/prop-types
const Status = ({value}) => {
  let type, color;
  switch (value) {
    case StockTakeStatuses.Added:
      type = "check-circle";
      color = "#52c41a";
      break;
    case StockTakeStatuses.Error:
      type = "close-circle";
      color = "#ff0000";
      break;
    default:
      return '';
  }
  return <Icon type={type}
               theme="twoTone"
               twoToneColor={color} />;
};
// eslint-disable-next-line react/prop-types
const Step2 = ({rowKey, stockTakes, submitting, done, onDone, onSubmit, onBack, getFieldDecorator, getFieldsError}) => {
  const columns = step2Columns.map(x => ({...x}));
  columns[0].render = value => <Status value={value}/>;
  columns[1].render = (value, row, index) => getFieldDecorator(
    `stockTakes_${index}_upc`,
    { initialValue: value }
  )(
    <Input readOnly />
  );
  columns[2].render = (value, row, index) => getFieldDecorator(
    `stockTakes_${index}_quantity`,
    {
      initialValue: value,
      rules: [{required: true, message: 'Quantity is required'}]
    }
  )(
    <InputNumber />
  );
  return (
    <Form onSubmit={onSubmit}>
      <ResponsiveTable columns={columns} rowKey={rowKey} dataSource={stockTakes} pagination={false}/>
      <FormItem>
        <ButtonGroup>
          {done && <Button
            type="primary"
            htmlType="button"
            onClick={onDone}
          >
            Next
          </Button>}
          {!done &&
          <Button
            type="primary"
            htmlType="submit"
            disabled={submitting || hasErrors(getFieldsError())}
          >
            Submit
          </Button>}
        </ButtonGroup>
      </FormItem>
    </Form>
  );
};

class BulkStockTakeForm extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    /* actions */
    takeStockTake: PropTypes.func.isRequired,
    getStock: PropTypes.func.isRequired,
    getProduct: PropTypes.func.isRequired,
    clearStock: PropTypes.func.isRequired,
    getStockTakeCounts: PropTypes.func.isRequired,
    /* state */
    salonId: PropTypes.string.isRequired,
    loading: PropTypes.bool.isRequired,
    form: PropTypes.object.isRequired,
    stock: PropTypes.object,
    product: PropTypes.object,
    stockTakeCounts: PropTypes.array.isRequired,
  };

  state = {
    step: 0,
    upcs: '',
    stockTakes: [],
    submitting: false,
    done: false,
  }

  componentDidMount() {
    const {step} = this.state;
    const {match: {params: {step: gotoStep}}} = this.props;
    if (step === 0) {
      this.setState({step: parseInt(gotoStep || '1', 10)});
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const {step} = this.state;
    const {match: {params: {step: gotoStep}}} = this.props;
    if (gotoStep !== prevProps.match.params.step && gotoStep !== step) {
      this.setState({step: parseInt(gotoStep || '1', 10)});
    }
  }

  goBack = () => {
    this.props.history.goBack();
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const {match: {params}} = this.props;
    const {stockTakeId} = decodeParams(params);
    this.props.form.validateFields(async(err, values) => {
      if (err) return;
      this.setState({submitting: true});
      let errors = 0;
      for (let i = 0; i < this.state.stockTakes.length; i++) {
        let status;
        try {
          const req = {
            stockTakeId,
            binId: null,
            sku: values[`stockTakes_${i}_upc`],
            quantity: values[`stockTakes_${i}_quantity`],
            verifySku: true,
          };
          await this.props.takeStockTake(req);
          status = StockTakeStatuses.Added;
        } catch (err) {
          errors++;
          status = StockTakeStatuses.Error;
        } finally {
          this.setState(prev => ({
            stockTakes: prev.stockTakes.map((x, j) => (j === i ? {...x, status} : {...x}))
          }));
        }
      }
      this.setState({submitting: false, done: errors === 0});
    });
  };

  handleStep1Submit = (e) => {
    e.preventDefault();
    const {match: {params: {stockTakeId}}} = this.props;
    this.props.form.validateFields(async(err, {upcs}) => {
      if (err) return;
      const stockTakesMap = upcs.trim().split('\n')
        .reduce(stockTakeReducer, {});
      const stockTakes = Object.keys(stockTakesMap).map(k => stockTakesMap[k]);
      this.setState({stockTakes});
      this.props.history.replace(`/stock-take/bulk-take/${stockTakeId}/2`);
    });
  }

  handleDone = (e) => {
    e.preventDefault();
    const {match: {params: {stockTakeId}}} = this.props;
    this.props.history.replace(`/stock-take/discrepancies/${stockTakeId}?workflow=bulk`);
  }

  render() {
    const {
      form: {getFieldDecorator, getFieldsError}
    } = this.props;
    const {step, upcs, stockTakes, done, submitting} = this.state;
    return (
      <Card title="Take Stock-take">
        {step === 1 && <Step1
          upcs={upcs}
          onSubmit={this.handleStep1Submit}
          onCancel={this.goBack}
          getFieldsError={getFieldsError}
          getFieldDecorator={getFieldDecorator}
        />}
        {step === 2 && <Step2
          rowKey="upc"
          stockTakes={stockTakes}
          done={done}
          submitting={submitting}
          onDone={this.handleDone}
          onSubmit={this.handleSubmit}
          onBack={this.goBack}
          getFieldsError={getFieldsError}
          getFieldDecorator={getFieldDecorator}
        />}
      </Card>
    );
  }
}

const BulkStockTakePage = Form.create()(BulkStockTakeForm);

const mapStateToProps = state => ({
  salonId: state.salon.selectedSalonId,
  loading: state.stockTake.takingStockTake || state.stockTake.gettingStock || state.stockTake.gettingStockTakeCounts,
  stock: state.stockTake.stock,
  product: state.product.product,
  stockTakeCounts: state.stockTake.stockTakeCounts,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  takeStockTake,
  getStock,
  getProduct,
  clearStock,
  getStockTakeCounts,
}, dispatch));

export default connect(mapStateToProps, mapDispatchToProps)(BulkStockTakePage);



// WEBPACK FOOTER //
// ./src/StockTake/BulkStockTakePage.js